import { APP_PATH, appPath, PORTFOLIO_FUND_PARAM, type ScreenId } from '@shared/config';

/**
 * 화면 트리 — BackBar 가 가는 "상위 화면"(DD-52). 브라우저 기록(뒤로 가기)이 아니라 주소만으로 정한다.
 * 문서: `work/project/d3-dashboard/design-preview/screens-app.md` "화면 트리".
 *
 * S04 메인
 * ├ S05 펀드 상세 ── S07 펀드 수정
 * │   └ S08 포트폴리오 상세(?fund=) ── S10 · S11 · S12(?fund= 유지)
 * ├ S08 포트폴리오 상세 ── S10 정보 수정 · S11 분기 데이터 입력 · S12 캡테이블
 * ├ S06 펀드 추가 · S09 포트폴리오 추가
 * ├ S13 · S14 · S15 · S16 · S18 · S19 · S20(헤더 · 계정 메뉴 화면)
 * └ S15 파일 관리 ── S17 예약 메일 설정
 *
 * 상위의 이름: 화면 이름(SCREEN_TITLE) 또는 대상 이름(펀드 · 회사 — 머리가 받아 쓴다).
 */
export type ScreenParent =
  | { kind: 'screen'; id: ScreenId; path: string }
  | { kind: 'fund'; id: number; path: string }
  | { kind: 'portfolio'; id: number; path: string };

interface ScreenLocation {
  params: Readonly<Record<string, string | undefined>>;
  search: URLSearchParams;
}

const positiveId = (value: string | null | undefined): number | undefined => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

const MAIN: ScreenParent = { kind: 'screen', id: 'S04', path: APP_PATH.root };

/** 회사 상세 하위 화면(S10 · S11 · S12)의 상위 = 그 회사 상세(주소의 ?fund= 를 이어 간다) */
function portfolioParent({ params, search }: ScreenLocation): ScreenParent {
  const id = positiveId(params.id);
  if (id === undefined) return MAIN;
  const fund = positiveId(search.get(PORTFOLIO_FUND_PARAM));
  return { kind: 'portfolio', id, path: appPath.portfolioDetail(id, { fund }) };
}

const PARENT: Partial<Record<ScreenId, (at: ScreenLocation) => ScreenParent>> = {
  S05: () => MAIN,
  S06: () => MAIN,
  S07: ({ params }) => {
    const id = positiveId(params.id);
    return id === undefined ? MAIN : { kind: 'fund', id, path: appPath.fundDetail(id) };
  },
  // 펀드로 거른 상세(?fund=)는 그 펀드 상세, 아니면 메인
  S08: ({ search }) => {
    const fund = positiveId(search.get(PORTFOLIO_FUND_PARAM));
    return fund === undefined ? MAIN : { kind: 'fund', id: fund, path: appPath.fundDetail(fund) };
  },
  S09: () => MAIN,
  S10: portfolioParent,
  S11: portfolioParent,
  S12: portfolioParent,
  S13: () => MAIN,
  S14: () => MAIN,
  S15: () => MAIN,
  S16: () => MAIN,
  S17: () => ({ kind: 'screen', id: 'S15', path: APP_PATH.file }),
  S18: () => MAIN,
  S19: () => MAIN,
  S20: () => MAIN,
};

/** 화면의 상위. 메인 · 로그인 화면 · 페이지 없음처럼 상위가 없으면 null */
export function screenParent(id: ScreenId, at: ScreenLocation): ScreenParent | null {
  return PARENT[id]?.(at) ?? null;
}
